import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Mail } from 'lucide-react';
import api from 'services/api';

const EmailValidation = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('validating');
  const [message, setMessage] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const validateEmail = async () => {
      try {
        setStatus('validating');
        const response = await api.get(`/access-requests/validate-email/${token}`);

        if (!isMounted) {
          return;
        }

        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message);
          setData(response.data.data);
        } else {
          setStatus('error');
          setMessage(response.data.message);
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setStatus('error');
        setMessage(error.response?.data?.message || 'Erro ao validar e-mail. Tente novamente.');
      }
    };

    validateEmail();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const getStatusIcon = () => {
    switch (status) {
      case 'validating':
        return <Clock className="h-16 w-16 text-blue-500 animate-spin" />;
      case 'success':
        return <CheckCircle className="h-16 w-16 text-green-500" />;
      case 'error':
        return <XCircle className="h-16 w-16 text-red-500" />;
      default:
        return <Mail className="h-16 w-16 text-gray-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'validating':
        return 'bg-blue-50 border-blue-200';
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'validating':
        return 'Validando e-mail...';
      case 'success':
        return 'E-mail Confirmado!';
      case 'error':
        return 'Erro na Validação';
      default:
        return 'Validação de E-mail';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className={`rounded-3xl border-2 p-8 text-center shadow-2xl backdrop-blur-sm ${getStatusColor()}`}>
          <div className="flex justify-center mb-8">
            <div className="relative">
              {getStatusIcon()}
              {status === 'success' && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              )}
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {getStatusTitle()}
          </h1>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-lg">
            <p className="text-gray-700 text-lg leading-relaxed">
              {message}
            </p>
          </div>

          {status === 'success' && data && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 mb-8 border border-green-200 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <span className="text-white text-sm font-bold">i</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Detalhes da Solicitação</h3>
              </div>
              <div className="space-y-3 text-left">
                <div className="flex justify-between items-center py-2 border-b border-green-200">
                  <span className="text-gray-600 font-medium">ID da Solicitação:</span>
                  <span className="text-gray-900 font-bold">#{data.access_request_id}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600 font-medium">E-mail:</span>
                  <span className="text-gray-900 font-bold">{data.email}</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {status === 'success' && (
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-4 px-8 rounded-2xl font-semibold text-lg hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Acessar o Sistema
                </button>
                <p className="text-sm text-gray-500">
                  Sua solicitação foi enviada para análise. Você receberá as credenciais por e-mail quando aprovada.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gradient-to-r from-gray-600 to-slate-600 text-white py-4 px-8 rounded-2xl font-semibold text-lg hover:from-gray-700 hover:to-slate-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Voltar ao Início
                </button>
                <p className="text-sm text-gray-500">
                  Se o problema persistir, entre em contato com o administrador.
                </p>
              </div>
            )}

            {status === 'validating' && (
              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                </div>
                <p className="text-sm text-gray-500">
                  Aguarde enquanto validamos seu email...
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-8">
          <div className="flex items-center justify-center space-x-2 text-gray-500">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">H</span>
            </div>
            <span className="text-sm font-medium">Horizon Group - Sistema de Gestão</span>
          </div>
          <p className="text-xs text-gray-400 mt-2">
            © 2025 Horizon Group. Todos os direitos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailValidation;
